import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/features/auth/auth.store';
import { formatNumber, formatRelative, parseApiDate } from '@/lib/format';
import { useSubjectLabel } from './subject-labels';
import {
  EXPORT_LIMIT,
  fetchAllMembers,
  useUsageMembers,
  type GroupSubject,
  type MemberDto,
  type MemberList,
  type UsageFilters,
} from './usage-analytics.api';
import { downloadCsv, type CsvValue } from './usage-csv';
import { Empty, ErrorNotice } from './usage-parts';

/**
 * The accounts behind a group.
 *
 * One sheet for two lists, because they are the same rows read two ways: who
 * visited in the range, and who visited just before it and has not since. The
 * second is the one marketing asks for, which is why the export lives here.
 */

function exportRows(rows: MemberDto[]): CsvValue[][] {
  return [
    ['First name', 'Last name', 'Email', 'Visits', 'Last visit (UTC)'],
    ...rows.map((row) => [
      row.firstName,
      row.lastName,
      row.email,
      row.visits,
      parseApiDate(row.lastVisitedOn)?.toISOString() ?? '',
    ]),
  ];
}

export function MembersSheet({
  filters,
  subject,
  list,
  onClose,
}: {
  filters: UsageFilters;
  subject: GroupSubject | null;
  list: MemberList;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const canOpenCustomers = useAuthStore((s) => s.can('isCustomerService'));
  const members = useUsageMembers(filters, subject, list);
  const rows = members.data?.pages.flat() ?? [];
  const [exporting, setExporting] = useState(false);

  const exportAll = async () => {
    if (!subject) return;
    setExporting(true);
    try {
      const all = await fetchAllMembers(filters, subject, list);
      downloadCsv(`${list}_${filters.start}_${filters.end}.csv`, exportRows(all));
      // Said rather than left to be discovered: a list that stops at a round
      // number looks complete to whoever is handed the file.
      if (all.length >= EXPORT_LIMIT) {
        toast.warning(
          t('usage.exportCapped', { count: EXPORT_LIMIT, formatted: formatNumber(EXPORT_LIMIT) })
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('usage.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Sheet open={subject !== null} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{subject ? label(subject) : ''}</SheetTitle>
          <SheetDescription>
            {t(list === 'lapsed' ? 'usage.lapsedHelp' : 'usage.membersHelp')}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 p-4">
          {rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={() => void exportAll()}
            >
              {exporting ? <Loader2 className="animate-spin" /> : <Download />}
              {t('usage.exportCsv')}
            </Button>
          )}

          {members.isLoading && <Skeleton className="h-40 rounded-xl" />}

          {members.isError && (
            <ErrorNotice
              message={members.error?.message || t('usage.loadFailed')}
              onRetry={() => void members.refetch()}
            />
          )}

          {members.isSuccess && rows.length === 0 && (
            <Empty>
              {t(list === 'lapsed' ? 'usage.noLapsed' : 'usage.noVisits')}
            </Empty>
          )}

          {rows.length > 0 && (
            <ul className="divide-y rounded-lg border">
              {rows.map((member) => {
                const name = [member.firstName, member.lastName]
                  .filter(Boolean)
                  .join(' ');
                const title = name || member.email || t('usage.unnamed');
                return (
                  <li
                    key={member.userId}
                    className="flex items-center gap-3 px-3 py-2"
                  >
                    <span className="min-w-0 flex-1">
                      {canOpenCustomers && member.email ? (
                        <Link
                          to={`/customers?q=${encodeURIComponent(member.email)}`}
                          className="block truncate text-sm font-medium hover:underline"
                        >
                          {title}
                        </Link>
                      ) : (
                        <span className="block truncate text-sm font-medium">
                          {title}
                        </span>
                      )}
                      {name && member.email && (
                        <span className="text-muted-foreground block truncate text-xs">
                          {member.email}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm tabular-nums">
                        {t('usage.visitCount', {
                          count: member.visits,
                          formatted: formatNumber(member.visits),
                        })}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {formatRelative(member.lastVisitedOn)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {members.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={members.isFetchingNextPage}
              onClick={() => void members.fetchNextPage()}
            >
              {members.isFetchingNextPage && <Loader2 className="animate-spin" />}
              {t('pageHistory.loadMore')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
